<template>
  <div :class="controlClasses" class="mds-form-control mds-select-chips">
    <label :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <div class="mds-select-chips-group">
          <VButton
            :class="selectChipsContainerClasses"
            @click.stop="openAddPopup"
          >
            <template v-if="isValueEmpty">
              <div class="placeholder">
                {{ placeholder }}
              </div>
            </template>

            <template v-else>
              <div
                v-for="(item, index) in value"
                :key="index"
                class="mds-select-chip"
              >
                <VButton
                  theme="link"
                  @click.stop="openDeletePopup($event, item.id)"
                >
                  <div v-if="item.name">
                    {{ item.name }}
                  </div>
                  <VButton
                    theme="link"
                    @click.stop="openDeletePopup($event, item.id)"
                  >
                    <template #icon>
                      <VIcon material="cancel" />
                    </template>
                  </VButton>
                </VButton>
              </div>
            </template>
          </VButton>

          <VButton ref="addBtn" @click="openAddPopup" title="Добавить">
            <template #icon>
              <VIcon material="add" />
            </template>
          </VButton>
        </div>
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <slot name="helper-text" />
        </div>
      </div>

      <VPopup
        show-pointer
        :target="addPopupTarget"
        :visible="addPopupVisible"
        class="mds-select-chips-popup"
        @keydown="keyDownListeners"
        @close="closeAddPopup"
        @update:visible="setFocusOut"
        position="bottom"
      >
        <VSelect
          ref="searchControl"
          layout="horizontal"
          :search-function="searchFunction"
          :placeholder="placeholder"
          :data-url="dataUrl"
          v-model="searchControlValue"
          @update:modelValue="onSearchControlInput"
        />
      </VPopup>

      <VPopup
        show-pointer
        ref="deletePopup"
        :target="deletePopupTarget"
        :visible="deletePopupVisible"
        class="mds-select-chips-approve-popup"
        @keydown="keyDownListeners"
        @close="closeDeletePopup"
      >
        <div class="body">
          <header>
            Подтверждаете удаление?
          </header>

          <div class="action-group">
            <VButton ref="deleteBtn" theme="primary" @click="deleteItem">
              Удалить
            </VButton>

            <VButton @click="closeDeletePopup">Отменить</VButton>
          </div>
        </div>
      </VPopup>
    </div>
  </div>
</template>

<style lang="scss" src="./VSelectChips.scss" />

<script lang="ts">
import VBaseControl from "@/core/services/VBaseControl";

import VSelect from "@/lib/components/VSelect/VSelect.vue";
import VButton from "@/lib/components/VButton/VButton.vue";
import VPopup  from "@/lib/components/VPopup/VPopup.vue";
import VIcon   from "@/lib/components/VIcon/VIcon.vue";

export default defineComponent({
  name: "VSelectChips",

  components: { VSelect, VButton, VIcon, VPopup },

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** v-model. Массив выбранных объектов. */
    modelValue: {
      type:    Array,
      default: () => [],
    },
    /** Внешняя функция поиска */
    searchFunction: {
      type:    Function,
      default: null,
    },
    /** Endpoint для загрузки опций */
    dataUrl: {
      type:    String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      /** Контрол находится в работе (открыт попап добавления/удаления) */
      isFocused:         false,
      addPopupTarget:    null,
      deletePopupTarget: null,
      /** Текущее значение внутреннего VSelect */
      searchControlValue: null,
      deleteEntityId:     null,
    };
  },

  computed: {
    /**
     * Нормализованное значение. Проп может прийти null (явная передача null
     * не подменяется дефолтом пропа), поэтому работаем с этим геттером, а не с
     * сырым modelValue — иначе .some/.findIndex/spread падают на null.
     */
    value(): unknown[] {
      return Array.isArray(this.modelValue) ? this.modelValue : [];
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      return result;
    },

    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.isFocused === true) classes.push("focused");
      if (this.isValueEmpty)       classes.push("empty");
      return classes;
    },

    selectChipsContainerClasses() {
      const classes = ["mds-select-chips-container"];
      if (this.isValueEmpty === false) classes.push("has-value");
      return classes;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    isValueEmpty(): boolean {
      return this.value.length === 0;
    },

    deletePopupVisible(): boolean {
      return this.deletePopupTarget !== null;
    },

    addPopupVisible(): boolean {
      return this.addPopupTarget !== null;
    },
  },

  watch: {
    modelValue: {
      handler: function () {
        this.validation.markDirty();
        this.validate();
      },
      deep: true,
    },
  },

  methods: {
    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this.value);
    },

    openAddPopup() {
      /**
       * Чтобы дропдаун в VSelect не прыгал — не открываем повторно.
       * Иначе провоцируется постоянное открытие/закрытие.
       */
      if (this.addPopupVisible === true) return;

      this.closeAllPopups();
      this.setFocusIn();
      this.addPopupTarget = this.$refs.addBtn.$el;
      this.focusOnSearchControl();
    },

    closeAddPopup() {
      this.addPopupTarget = null;
      this.setFocusOut();
    },

    openDeletePopup(event, itemId) {
      this.closeAllPopups();
      this.setFocusIn();
      this.deletePopupTarget = event.target;
      this.deleteEntityId    = itemId;

      /** Следующий тик для того чтобы попап успел смонтироваться и фокус
       * на элементе отработал успешно. Фокус на элемент нужно повешать
       * для того, чтобы события keydowns, например такое как escape, были бы обработаны попапом,
       * а иначе попап не понимает что произшёл escape, и не понимает что нужно эмитить наверх  */
      this.$nextTick(() => {
        this.$refs.deleteBtn.$el.focus();
      });
    },

    closeDeletePopup() {
      this.deletePopupTarget = null;
      this.setFocusOut();
    },

    deleteItem() {
      const index = this.value.findIndex((_) => _.id === this.deleteEntityId);
      const newValue = [...this.value];
      newValue.splice(index, 1);
      this.$emit('update:modelValue', newValue);
      this.setFocusOut();
      this.closeAllPopups();
    },

    keyDownListeners(event) {
      if (event.target.tagName !== "INPUT" || event.key === "Enter") {
        event.preventDefault();
      }
      switch (event.key) {
        case "Escape":
          this.setFocusOut();
          this.closeAllPopups();
          break;
      }
    },

    focusOnSearchControl() {
      this.$nextTick(() => {
        this.$refs.searchControl.$el.querySelector("input").focus();
      });
    },

    setFocusOut() {
      this.isFocused = false;
    },

    setFocusIn() {
      this.validation.markTouched();
      this.isFocused = true;
    },

    onSearchControlInput(newValue) {
      if (!newValue) return;

      const isUniq = this.value.some((_) => _.id === newValue.id) === false;

      if (isUniq === true) {
        const updatedValue = [...this.value, newValue];
        this.$emit('update:modelValue', updatedValue);
        this.searchControlValue = null;
      }

      this.closeAllPopups();
    },

    closeAllPopups() {
      this.closeAddPopup();
      this.closeDeletePopup();
    },
  },
});
</script>
